import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { IconTime } from '@americanexpress/dls-icons';
import { Label, SelectNative, SelectNativeOption } from '@americanexpress/dls-react';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { getStateKey } from '../../selectors/commonSelectors';
import {
  getDefaultTime,
  getHasAnytime,
  getIs24Hour,
  getMinutesIntervalPerOption,
  getEndTime,
  getStartTime,
} from '../../selectors/timeSelectors';
import styles from './TimeDropdown.scss';
import { useTimeDispatch, useTimeSelector } from '../../context/timeContext';
import { useDateSelector } from '../../context/dateContext';
import ANYTIME from '../../constants/timeDropdownConstants';
import { getEndDate, getStartDate } from '../../selectors/dateSelectors';
import { isValidTime, getHourLabels } from '../../utils/timeUtils';
import {
  END_TIME,
  START_TIME,
} from '../../constants/dateConstants';
import { useIsHomePageVariantHook } from '../../hooks/useIsHomePageVariantHook';

const TimeDropdown = ({
  stateKey,
  ariaLabel,
  onChangeAction,
  className,
}) => {
  const { formatMessage } = useIntl();
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);
  const dispatch = useTimeDispatch();
  const is24Hour = useTimeSelector(getIs24Hour);
  const hasAnytime = useTimeSelector(getHasAnytime);
  const minutesIntervalPerOption = useTimeSelector(getMinutesIntervalPerOption);
  const defaultTime = useTimeSelector(getDefaultTime);
  const stateValueFromContext = useTimeSelector(getStateKey(stateKey));
  const stateValue = stateValueFromContext || (hasAnytime ? ANYTIME : defaultTime);
  const isMobile = useMediaBreakpoint('sm-down');
  const activeLocale = useSelector(getActiveLocaleSelector);
  const currentDate = useMemo(
    () => new Date().toLocaleDateString(activeLocale),
    [activeLocale]
  );

  const handleChange = ({ target: { value: newValue } }) => {
    dispatch(onChangeAction(newValue));
  };

  const anytimeLabel = formatMessage({ id: 'anytime' });
  const currentStartTime = hasAnytime ? startTime : startTime || defaultTime;

  const formattedStartDate = useMemo(() => new Date(startDate)
    .toLocaleDateString(activeLocale),
  [startDate, activeLocale]);
  const formattedEndDate = useMemo(() => new Date(endDate)
    .toLocaleDateString(activeLocale),
  [endDate, activeLocale]);

  const showPastHoursOfDay = useMemo(() => {
    if (
      (stateKey === START_TIME && formattedStartDate === currentDate)
      || (stateKey === END_TIME && formattedEndDate === currentDate)
    ) {
      return false;
    }
    return true;
  }, [stateKey, startDate, endDate, currentDate]);

  const options = useMemo(() => getHourLabels(anytimeLabel, {
    is24Hour,
    minutesIntervalPerOption,
    hasAnytime,
    showPastHoursOfDay,
    startTime: formattedStartDate === formattedEndDate && stateKey === END_TIME
      ? currentStartTime
      : null,
  }), [
    stateKey,
    formattedEndDate,
    formattedStartDate,
    showPastHoursOfDay,
    anytimeLabel,
    hasAnytime,
    is24Hour,
    minutesIntervalPerOption,
    currentStartTime,
  ]);

  useEffect(() => {
    if (
      !isValidTime({
        startDate,
        endDate,
        startTime: currentStartTime,
        endTime,
        value: stateValue,
        stateKey,
      })
      && stateKey === END_TIME
    ) {
      dispatch(onChangeAction(currentStartTime));
    }
  }, [startDate, endDate, currentStartTime]);

  const isVariant = useIsHomePageVariantHook();

  return (
    <div className={clsx(styles.timeDropdownWrapper, className, 'flex flex-wrap flex-column')}>
      {
        isVariant ? (
          <Label htmlFor={`date-input-${stateKey}`} className="margin-1-b">{formatMessage({ id: ariaLabel })}</Label>
        ) : (
          <div className="flex flex-align-center">
            <IconTime
              title="Time Icon"
              titleId={`${stateKey}-timeIcon`}
              color="neutral"
              size="sm"
            />
            <label htmlFor={`date-input-${stateKey}`} className="body color-text-subtle margin-0">
              {formatMessage({ id: ariaLabel })}
            </label>
          </div>
        )
      }
      <SelectNative
        value={stateValue}
        onChange={handleChange}
        aria-label={ariaLabel}
        id={`date-input-${stateKey}`}
        fluid={isMobile}
        className={clsx(styles.timeDropdown, 'flex-item-grow')}
      >
        {options.map(({ label, value }) => (
          <SelectNativeOption
            key={value}
            value={value}
          >
            {label}
          </SelectNativeOption>
        ))}
      </SelectNative>
    </div>
  );
};

TimeDropdown.propTypes = {
  stateKey: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onChangeAction: PropTypes.func.isRequired,
  className: PropTypes.string,
};

TimeDropdown.defaultProps = {
  className: '',
};

export { TimeDropdown as TestableTimeDropdown };

export default TimeDropdown;
